import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import deliveryApi from '../../api/deliveryApi';
import { getErrorMessage } from '../../utils/errorHandler';
import DeliveryDistrictFormDialog from '../../sections/admin/delivery/DeliveryDistrictFormDialog';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });

// Administracion de los distritos de Lima con costo de delivery propio (express/normal).
// Fuera de estos distritos ("provincia") el checkout no cobra nada, se coordina por courier.
export default function DeliveryDistrictsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [districtToDelete, setDistrictToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDistricts = async () => {
    setLoading(true);
    try {
      const { data } = await deliveryApi.getDeliveryDistricts();
      setDistricts(data.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de distritos.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => {
    setEditingDistrict(null);
    setFormOpen(true);
  };

  const handleEdit = (district) => {
    setEditingDistrict(district);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchDistricts();
  };

  const handleConfirmDelete = async () => {
    if (!districtToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await deliveryApi.deleteDeliveryDistrict(districtToDelete.id);
      enqueueSnackbar('Distrito eliminado correctamente.', { variant: 'success' });
      setDistrictToDelete(null);
      fetchDistricts();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el distrito.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'district', headerName: 'Distrito', flex: 1, minWidth: 200 },
      {
        field: 'price_express',
        headerName: 'Delivery Express (S/)',
        width: 190,
        valueFormatter: (value) => currencyPen.format(value || 0),
      },
      {
        field: 'price_normal',
        headerName: 'Delivery Normal (S/)',
        width: 190,
        valueFormatter: (value) => currencyPen.format(value || 0),
      },
      {
        field: 'is_active',
        headerName: 'Estado',
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Activo' : 'Inactivo'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setDistrictToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Delivery</Typography>
          <Typography variant="body2" color="text.secondary">
            Costo de envío por distrito, solo dentro de Lima. Fuera de estos distritos el envío se
            coordina por courier, sin costo fijo en el sistema.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo distrito
        </Button>
      </Stack>

      <Card>
        <DataGrid autoHeight rows={districts} columns={columns} loading={loading} disableRowSelectionOnClick />
      </Card>

      <DeliveryDistrictFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        district={editingDistrict}
      />

      <Dialog open={Boolean(districtToDelete)} onClose={() => setDistrictToDelete(null)}>
        <DialogTitle>Eliminar distrito</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{districtToDelete?.district}</strong>? Los pedidos ya
            realizados conservan su propio costo de delivery, sin verse afectados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDistrictToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
